import { variableTokenHtml } from '../functions';

// Gère le clic sur le bouton de suppression d'une variable
function handleDeleteClick(e) {
    // Récupère le jeton parent
    const token = e.target.parentNode;

    // Supprime le jeton de l'éditeur
    if (token && token.parentNode) {
        token.parentNode.removeChild(token);
    }

    // Empêche la propagation de l'événement pour éviter des effets indésirables
    e.stopPropagation();
}

// Met à jour l'affichage des jetons de variable avec les valeurs actuelles
export function updateVariableTokens(editor) {
    if (typeof editor === 'undefined') {
        editor = document.querySelector('.enhanced-editor');
    }
    if (typeof editor === 'undefined' || editor === null) {
        return;
    }
    const variableTokens = editor.querySelectorAll('.variable-token');

    variableTokens.forEach(token => {
        const variableName = token.getAttribute('data-variable');
        const value = getElementValueForEmail(variableName);

        // Met à jour uniquement le contenu textuel (avant le bouton de suppression)
        const textNode = Array.from(token.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
        if (textNode) {
            if (value && value.trim() !== '') {
                textNode.nodeValue = value;
            } else {
                textNode.nodeValue = variableName;
            }
            if (textNode.nodeValue === variableName) {
                token.classList.add('--unfilled');
            } else {
                token.classList.remove('--unfilled');
            }
        }
    });
}

function getElementValueForEmail(email) {
    // Sélectionne les éléments avec un attribut data-email (sans suffixe spécifique)
    const elements = document.querySelectorAll('[data-email]');

    // Trouver l'élément visible qui correspond à l'email (priorité aux éléments visibles)
    let element = null;
    for (let el of elements) {
        const emailAttr = Object.keys(el.dataset).find(key => key.startsWith('email') && el.dataset[key] === email);
        if (emailAttr) {
            if (el.offsetParent !== null) {
                // Élément visible : on le prend immédiatement
                element = el;
                break;
            }
            // Élément caché : on le garde en fallback si rien de visible trouvé
            if (!element) {
                element = el;
            }
        }
    }

    if (!element) {
        // Fallback : valeur stockée au chargement pour les variables sans champ de formulaire
        if (window.leadRequestEmailData?.[email] !== undefined) {
            return String(window.leadRequestEmailData[email]);
        }
        return "";
    }

    // Récupère le suffixe du data-email (par exemple, -2, -3...)
    const matchingAttr = Object.keys(element.dataset).find(attr => attr.startsWith("email") && element.dataset[attr] === email);
    const suffix = matchingAttr ? matchingAttr.replace("email", "") : "";

    if (element.tagName === "SELECT") {
        const selectedOption = element.options[element.selectedIndex];

        // Récupère le data-for-email correspondant avec le suffixe si disponible
        const dataForEmail = selectedOption.getAttribute(`data-for-email${suffix}`);

        // Vérifier si 'data-for-email' est défini et renvoyer sa valeur, sinon renvoyer la valeur de l'option
        let result = dataForEmail !== null ? dataForEmail : selectedOption.value;

        // cas spécifique pour "demande_administratif_type" on ajoute la valeur de "demande_administratif_type_other"
        if (email === 'demande_administratif_type') {
            const element2 = getElementValueForEmail('demande_administratif_type_other');
            if (element2) {
                result += ' (' + element2 + ')';
            }
        }
        return result;

    }

    if (element.type === "checkbox") {
        return element.checked ? "Mme" : "M."; // Mme si coché, M. si décoché
    }

    return element.value; // Pour les inputs
}

// Gestionnaire d'événement pour le collage
// Whitelist stricte : on ne garde que <strong>/<b>/<em>/<i>/<br>, tout le reste
// est aplati en texte. Aucun `style`, `class`, `bgcolor` ni balise structurelle
// ne survit — le rendu final reste prévisible côté destinataire.
const PASTE_ALLOWED_TAGS = new Set(['STRONG', 'B', 'EM', 'I', 'BR']);
const PASTE_BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'TR']);

function sanitizePastedNode(node, out) {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue.replace(/\u00A0/g, ' ').replace(/[ \t\r\n]+/g, ' ');
        if (text !== '') {
            out.appendChild(document.createTextNode(text));
        }
        return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName;
    if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'META' || tag === 'LINK'
        || tag === 'TITLE' || tag === 'IMG' || tag === 'SVG') {
        return;
    }

    if (PASTE_ALLOWED_TAGS.has(tag)) {
        const clean = document.createElement(tag.toLowerCase());
        node.childNodes.forEach(child => sanitizePastedNode(child, clean));
        out.appendChild(clean);
        return;
    }

    // Balise non whitelistée : on aplati son contenu.
    node.childNodes.forEach(child => sanitizePastedNode(child, out));

    // Les blocs (p, div, li…) ajoutent un saut de ligne implicite entre eux.
    if (PASTE_BLOCK_TAGS.has(tag) && out.lastChild && out.lastChild.nodeName !== 'BR') {
        out.appendChild(document.createElement('br'));
    }
}

function handlePaste(event) {
    event.preventDefault();

    const clipboardData = event.clipboardData || window.clipboardData;
    const pastedHTML = clipboardData.getData('text/html');
    const pastedText = clipboardData.getData('text/plain');

    const fragment = document.createDocumentFragment();

    if (pastedHTML) {
        // Document inerte : contrairement à innerHTML sur un élément de la page, rien n'y est chargé ni exécuté
        // (images, handlers) avant le tri de sanitizePastedNode.
        const parsed = new DOMParser().parseFromString(pastedHTML, 'text/html');
        Array.from(parsed.body.childNodes).forEach(child => sanitizePastedNode(child, fragment));
    } else if (pastedText) {
        const cleaned = pastedText.replace(/\r\n?/g, '\n').replace(/\u00A0/g, ' ');
        const lines = cleaned.split('\n');
        lines.forEach((line, index) => {
            if (line !== '') {
                fragment.appendChild(document.createTextNode(line));
            }
            if (index < lines.length - 1) {
                fragment.appendChild(document.createElement('br'));
            }
        });
    } else {
        return;
    }

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(fragment);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
}

// Le :empty CSS ne suffit pas : contenteditable laisse un <br> résiduel une fois le texte effacé,
// et le placeholder (::before) ne revient plus. La classe .--is-empty est donc pilotée en JS, par un
// MutationObserver qui couvre aussi les insertions programmatiques (clic sur une variable…).
function bindEmptyState(editor) {
    if (editor._emptyStateBound) {
        return;
    }
    editor._emptyStateBound = true;

    const refresh = () => {
        const hasContent = Array.from(editor.children).some(child => child.tagName !== 'BR');
        const isEmpty = editor.textContent.trim() === '' && !hasContent;
        editor.classList.toggle('--is-empty', isEmpty);

        return isEmpty;
    };
    refresh();
    new MutationObserver(refresh).observe(editor, { childList: true, subtree: true, characterData: true });
    // Au focus d'un éditeur vide, <br> retiré et curseur en début de champ : sinon il se cale après le
    // <br>, donc après le placeholder. Vide recalculé ici et non lu sur la classe : l'observer est
    // asynchrone, et un clic sur une variable insère le jeton puis redonne le focus avant son passage
    // (la classe dirait encore « vide » et le jeton serait effacé).
    editor.addEventListener('focus', () => {
        if (!refresh() || editor.innerHTML === '') {
            return;
        }
        editor.innerHTML = '';
        const range = document.createRange();
        range.setStart(editor, 0);
        range.collapse(true);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
    });
}

export function initializeEditor(element, templateSelector = '.email-template', allowedVariables = null, options = {}) {

    const tokensOnly = options.tokensOnly === true;

    const editor = element.querySelector('.enhanced-editor');
    const templateInput = element.querySelector(templateSelector);
    const variableItems = element.querySelectorAll('.variable-item');
    const emailAnswerTemplate = templateInput.value;
    // Liste de variables autorisées par l'autocomplétion `{var}` : si passée explicitement,
    // remplace la globale `availableVariables` (qui sert pour les templates email).
    const editorAllowedVariables = Array.isArray(allowedVariables) ? allowedVariables : (window.availableVariables ?? []);

    // Convertit les variables {{variable}} en jetons HTML interactifs.
    // Deux tokens consécutifs (`{{a}}{{b}}`) sont séparés par un espace : sans ça,
    // la valeur substituée à l'envoi s'accole (ex: "MonsieurDupont").
    const spacedTemplate = emailAnswerTemplate.replace(/}}(\s*){{/g, (_, ws) => ws.length ? '}}' + ws + '{{' : '}} {{');

    // Insère le contenu formaté dans l'éditeur
    editor.innerHTML = spacedTemplate.replace(/\{\{([\p{L}0-9_\s]+)\}\}/gu, (match, variableName) => {
        return variableTokenHtml(variableName);
    });

    if (tokensOnly) {
        editor.addEventListener('keydown', e => e.preventDefault());
        editor.addEventListener('paste', e => e.preventDefault());
    } else {
        editor.removeEventListener('paste', handlePaste);
        editor.addEventListener('paste', handlePaste);

        function handleKeyDown(e)
        {
            const selection = window.getSelection();
            if (selection.rangeCount) {
                const range = selection.getRangeAt(0);
                const startContainer = range.startContainer;
                const endContainer = range.endContainer;

                // Vérifie si la sélection commence ou se termine dans un jeton
                const startInToken = isInVariableToken(startContainer);
                const endInToken = isInVariableToken(endContainer);

                if (startInToken || endInToken) {
                    // Si la touche est Supprimer ou Backspace, permet la suppression
                    if (e.key === 'Delete' || e.key === 'Backspace') {
                        // Si toute la variable est sélectionnée, permet la suppression
                        if (range.startOffset === 0 && range.endOffset === startContainer.length) {
                            return;
                        }

                        // Sinon, empêche la modification partielle
                        e.preventDefault();
                    } else {
                        // Pour toutes les autres touches, empêche l'édition
                        e.preventDefault();
                    }
                }
            }
        }

        // Vérifie s'il y a déjà un eventListener attaché et le retire
        if (editor._keydownHandler) {
            editor.removeEventListener('keydown', editor._keydownHandler);
        }

        // Stocke la nouvelle fonction dans l'élément
        editor._keydownHandler = handleKeyDown;

        // Ajoute l'event listener
        editor.addEventListener('keydown', handleKeyDown);

        // Système d'autocomplétion: détection de {variable} et transformation automatique en jeton
        editor.addEventListener('keydown', function(e) {
            // Si on appuie sur }
            if (e.key === '}') {
                const selection = window.getSelection();
                if (!selection.rangeCount) return;

                const range = selection.getRangeAt(0);
                const textNode = range.startContainer;

                // Vérifie que nous sommes dans un nœud texte
                if (textNode.nodeType === Node.TEXT_NODE) {
                    const caretPos = range.startOffset;
                    const text = textNode.nodeValue;

                    // Cherche un motif {variable} juste avant le curseur
                    const match = /\{([\p{L}0-9_\s]+)$/u.exec(text.substring(0, caretPos));

                    if (match) {
                        const variableName = match[1];

                        // Vérifie si la variable existe dans la liste des variables disponibles
                        if (editorAllowedVariables.includes(variableName)) {
                            // Empêche l'insertion de l'espace ou du retour à la ligne
                            e.preventDefault();

                            // Calcule où commencer la sélection (juste avant {)
                            let startPos = caretPos - (variableName.length + 2);
                            if (startPos < 0) startPos = 0; // Empêche les valeurs négatives

                            // Sélectionne {variable}
                            const newRange = document.createRange();
                            newRange.setStart(textNode, startPos);
                            newRange.setEnd(textNode, caretPos);
                            selection.removeAllRanges();
                            selection.addRange(newRange);

                            // Insère le jeton de variable
                            insertVariableAtCaret(variableName, match[0]);
                            updateVariableTokens(editor);
                        }
                    }
                }
            }
        });
    }

    // Vérifie si un nœud est à l'intérieur d'un jeton de variable
    function isInVariableToken(node) {
        if (!node) return false;
        // Remonte l'arborescence DOM jusqu'à trouver un jeton ou l'éditeur
        let current = node;
        while (current && current !== editor) {
            if (current.classList && current.classList.contains('variable-token')) {
                return true;
            }
            current = current.parentNode;
        }
        return false;
    }

    // Ajoute des écouteurs d'événements pour les variables cliquables
    variableItems.forEach(item => {
        item.addEventListener('click', () => {
            const variableName = item.getAttribute('data-variable');
            insertVariableAtCaret(variableName);
        });
    });

    // Ajoute des écouteurs aux boutons de suppression des variables
    function addDeleteButtonListeners() {
        const deleteButtons = editor.querySelectorAll('.variable-token .delete-btn');

        deleteButtons.forEach(btn => {
            // Supprime les anciens écouteurs pour éviter les doublons
            btn.removeEventListener('click', handleDeleteClick);
            // Ajoute le nouvel écouteur
            btn.addEventListener('click', handleDeleteClick);
        });
    }

    // Insère une variable à la position du curseur dans l'éditeur
    function insertVariableAtCaret(variableName, replaceText = null) {
        const selection = window.getSelection();

        // Curseur hors de l'éditeur (jamais cliqué, message vide) : insertion en fin de contenu, pour
        // pouvoir commencer le message par une variable
        if (!selection.rangeCount || !editor.contains(selection.getRangeAt(0).commonAncestorContainer)) {
            editor.focus();
            const endRange = document.createRange();
            endRange.selectNodeContents(editor);
            endRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(endRange);
        }

        if (selection.rangeCount) {
            const range = selection.getRangeAt(0);

            // Vérifie que nous sommes bien dans l'éditeur
            if (editor.contains(range.commonAncestorContainer)) {
                // Si on a du texte à remplacer (comme {prenom})
                if (replaceText) {
                    // Supprime le texte à remplacer
                    range.deleteContents();
                }

                // Crée le HTML du jeton de variable
                const tokenHTML = variableTokenHtml(variableName);

                // Insère le jeton HTML à la position du curseur
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = tokenHTML;
                const tokenNode = tempDiv.firstChild;

                // Force un espace si le voisin immédiat est déjà un token :
                // sans ça, deux variables consécutives fusionnent leurs valeurs
                // substituées à l'envoi (ex: "MonsieurDupont").
                const neighborIsToken = (node) => node && node.nodeType === Node.ELEMENT_NODE
                    && node.classList && node.classList.contains('variable-token');
                const prevSibling = range.startContainer.nodeType === Node.TEXT_NODE
                    ? (range.startOffset === 0 ? range.startContainer.previousSibling : null)
                    : range.startContainer.childNodes[range.startOffset - 1];
                const nextSibling = range.startContainer.nodeType === Node.TEXT_NODE
                    ? (range.startOffset === range.startContainer.length ? range.startContainer.nextSibling : null)
                    : range.startContainer.childNodes[range.startOffset];

                if (neighborIsToken(prevSibling)) {
                    range.insertNode(document.createTextNode(' '));
                    range.collapse(false);
                }

                range.insertNode(tokenNode);

                if (neighborIsToken(nextSibling)) {
                    const spaceAfter = document.createTextNode(' ');
                    tokenNode.parentNode.insertBefore(spaceAfter, tokenNode.nextSibling);
                }

                // Place le curseur juste après le jeton inséré
                range.setStartAfter(tokenNode);
                range.setEndAfter(tokenNode);
                selection.removeAllRanges();
                selection.addRange(range);

                // Ajoute les écouteurs d'événements au bouton de suppression
                addDeleteButtonListeners();
            }
        }

        // S'assure que l'éditeur garde le focus
        editor.focus();
    }

    // Convertit le contenu enrichi en format de template brut (avec {{variable}})
    function getTemplateContent() {
        // Crée un clone du contenu de l'éditeur pour ne pas modifier l'original
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = editor.innerHTML;

        // Remplace tous les jetons de variable par la syntaxe {{variable}}
        const variableTokens = tempDiv.querySelectorAll('.variable-token');
        variableTokens.forEach(token => {
            const variableName = token.getAttribute('data-variable');
            token.outerHTML = `{{${variableName}}}`;
        });

        return tempDiv.innerHTML;
    }


    const saveBtn = document.querySelector('.save-email');
    if (saveBtn) {
        // Enregistre le template final (conversion en version brute)
        saveBtn.addEventListener('click', () => {
            templateInput.value = getTemplateContent();
            return false;
        });
    }
    // Ajoute les écouteurs aux boutons de suppression existants
    addDeleteButtonListeners();
    bindEmptyState(editor);
}

export function getEditorContent(editor) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editor.innerHTML;
    tempDiv.querySelectorAll('.variable-token').forEach(token => {
        const variableName = token.getAttribute('data-variable');
        token.outerHTML = `{{${variableName}}}`;
    });
    return tempDiv.innerHTML;
}

